// /cave/profile — a signed-in member's own crew card: a live preview of how
// they'll look to everyone else (flag, crew name, read-only rank, motto),
// then the fields they may edit themselves (crew name, flag, motto,
// favourite ship, flagship name). Rank/section/role are set by the officers
// on the member's admin page in the cave (CrewMemberPage.tsx), not here.
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getFlagList, getMyProfile, updateMyProfile } from '../api.js';
import { Button } from '../components/Button.js';
import { ShipPicker } from '../components/ShipPicker.js';
import { CLAN_FLAGS, MOTTO_MAX, ROLE_LABELS, SECTION_LABELS, changedFields } from '../site/pages/profileEditing.js';
import styles from './MyProfilePage.module.css';

const MAX_FLAGSHIP_NAME = 40;

interface EditableProfile {
  name: string;
  flag: string;
  motto: string;
  favouriteShip: string | null;
  flagshipName: string | null;
}

export function MyProfilePage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['me', 'profile'], queryFn: getMyProfile });
  const flagsQuery = useQuery({ queryKey: ['flags'], queryFn: getFlagList });

  const [initialized, setInitialized] = useState(false);
  const [original, setOriginal] = useState<EditableProfile | null>(null);
  const [name, setName] = useState('');
  const [flag, setFlag] = useState('');
  const [motto, setMotto] = useState('');
  const [favouriteShip, setFavouriteShip] = useState<string | null>(null);
  const [flagshipName, setFlagshipName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed local editing state once from the server's answer — an effect, not
  // read-during-render, so a later background refetch (e.g. after another
  // tab's save) never stomps whatever the member is mid-editing here.
  useEffect(() => {
    if (query.data && !initialized) {
      setOriginal({
        name: query.data.name,
        flag: query.data.flag,
        motto: query.data.motto,
        favouriteShip: query.data.favouriteShip,
        flagshipName: query.data.flagshipName,
      });
      setName(query.data.name);
      setFlag(query.data.flag);
      setMotto(query.data.motto);
      setFavouriteShip(query.data.favouriteShip);
      setFlagshipName(query.data.flagshipName ?? '');
      setInitialized(true);
    }
  }, [query.data, initialized]);

  const flagOptions = useMemo(() => {
    const uploaded = flagsQuery.data?.flags ?? [];
    return [...CLAN_FLAGS, ...uploaded.filter((f) => !CLAN_FLAGS.includes(f))];
  }, [flagsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!original) throw new Error('Not loaded yet.');
      const trimmedName = name.trim();
      const trimmedMotto = motto.trim();
      const trimmedFlagshipName = flagshipName.trim();
      const edited: EditableProfile = {
        name: trimmedName,
        flag,
        motto: trimmedMotto,
        favouriteShip,
        flagshipName: trimmedFlagshipName === '' ? null : trimmedFlagshipName,
      };
      return updateMyProfile(changedFields(original, edited));
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'profile'], data);
      setOriginal({
        name: data.name,
        flag: data.flag,
        motto: data.motto,
        favouriteShip: data.favouriteShip,
        flagshipName: data.flagshipName,
      });
      setName(data.name);
      setFlag(data.flag);
      setMotto(data.motto);
      setFavouriteShip(data.favouriteShip);
      setFlagshipName(data.flagshipName ?? '');
      setError(null);
      setSaved(true);
    },
    onError: (err) => {
      setSaved(false);
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      setError(message === 'name taken' ? 'That name is already taken.' : message);
    },
  });

  function pickShip(id: string | null) {
    setSaved(false);
    setFavouriteShip(id);
  }

  function handleNameChange(value: string) {
    setSaved(false);
    setName(value.slice(0, 60));
  }

  function handleMottoChange(value: string) {
    setSaved(false);
    setMotto(value.slice(0, MOTTO_MAX));
  }

  function handleFlagChange(value: string) {
    setSaved(false);
    setFlag(value);
  }

  function handleFlagshipNameChange(value: string) {
    setSaved(false);
    setFlagshipName(value.slice(0, MAX_FLAGSHIP_NAME));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaved(false);
    setError(null);
    saveMutation.mutate();
  }

  if (query.isLoading) return <p>Loading…</p>;
  if (query.isError) {
    return <p className={styles.error}>{(query.error as Error).message}</p>;
  }

  const profile = query.data;

  return (
    <section>
      <h2 className={styles.heading}>My profile</h2>

      {profile && (
        <div className={styles.preview} aria-label="How your profile looks to others">
          <img className={styles.previewFlag} src={flag || profile.flag} alt="" />
          <div>
            <p className={styles.previewRank}>{profile.rank}</p>
            <p className={styles.previewName}>{name || profile.name}</p>
            {motto.trim() && <p className={styles.previewMotto}>{motto.trim()}</p>}
          </div>
        </div>
      )}

      {profile?.slug && (
        <p className={styles.viewLink}>
          <Link to={`/crew/${profile.slug}`}>View your public profile</Link>
        </p>
      )}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label htmlFor="crew-name">Crew name</label>
          <input id="crew-name" value={name} maxLength={60} onChange={(e) => handleNameChange(e.target.value)} />
        </div>

        <div className={styles.field}>
          <span>Flag</span>
          <div className={styles.flagGrid} role="radiogroup" aria-label="Flag">
            {flagOptions.map((f) => (
              <button
                key={f}
                type="button"
                role="radio"
                aria-checked={flag === f}
                className={flag === f ? `${styles.flagTile} ${styles.flagTileSelected}` : styles.flagTile}
                onClick={() => handleFlagChange(f)}
              >
                <img src={f} alt="" />
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="motto">Motto</label>
          <input
            id="motto"
            value={motto}
            maxLength={MOTTO_MAX}
            placeholder="A short line under your name"
            onChange={(e) => handleMottoChange(e.target.value)}
          />
          <p className={styles.hint}>
            {motto.length} / {MOTTO_MAX}
          </p>
        </div>

        <div className={styles.field}>
          <span>Most beloved ship</span>
          <ShipPicker value={favouriteShip} onChange={pickShip} />
        </div>

        <div className={styles.field}>
          <label htmlFor="flagship-name">Flagship name</label>
          <input
            id="flagship-name"
            value={flagshipName}
            maxLength={MAX_FLAGSHIP_NAME}
            placeholder="The name you'd give your flagship"
            onChange={(e) => handleFlagshipNameChange(e.target.value)}
          />
          <p className={styles.hint}>
            {flagshipName.length} / {MAX_FLAGSHIP_NAME}
          </p>
        </div>

        {profile && (
          <div className={styles.readOnly}>
            <span className={styles.readOnlyLabel}>Rank / section / role — set by the officers</span>
            <p className={styles.readOnlyValue}>
              {profile.rank} · {SECTION_LABELS[profile.section]} · {ROLE_LABELS[profile.role]}
            </p>
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}
        {saved && !saveMutation.isPending && <p className={styles.saved}>Saved.</p>}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={saveMutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </section>
  );
}
